import React, { useMemo } from 'react';
import ArcGraphCoachView from '../../../components/coach/arc/ArcGraphCoachView';
import ChartContainer from '../../../components/coach/charts/common/ChartContainer';
import { useArcSessionStrokeSamples } from '../../../hooks/useArcMetrics';
import { ARC_PHYSICS } from '../../../features/dashboards/arcConfig';
import type { StrokeSample } from '../../../features/dashboards/rowingArcTypes';
import {
  hullSetFilterSubtitle,
  useHullStabilityFilterOptional,
} from '../../../contexts/HullStabilityFilterContext';
import { isStrokeIndexAllowed } from '../../../utils/hullStability/strokeAlignment';
import type { GraphComponentProps } from '../registry';

const ARC_SUBTITLE = 'Catch/finish arc geometry and seat-level stroke profile.';

/**
 * Wrapper for Rowing Arc (v1).
 *
 * Uses session stroke samples and renders the full coach arc experience.
 * Prototype-only data source toggles are intentionally excluded.
 */
const RowingArcWrapper: React.FC<GraphComponentProps> = ({
  sessionId,
  rowerContext,
  tileMode = true,
}) => {
  /** Page-flow layout (starter dashboard); false = fixed tile with inner scroll (builder / grid). */
  const flowLayout = tileMode === false;
  const hullFilter = useHullStabilityFilterOptional();
  const sessionQuery = useArcSessionStrokeSamples(sessionId, {
    enabled: !!sessionId,
  });

  const filteredStrokes = useMemo(() => {
    const strokes = sessionQuery.data ?? [];
    if (!hullFilter?.isFilterActive || !hullFilter.allowedStrokeIndices) {
      return strokes;
    }
    return strokes.filter((stroke) =>
      isStrokeIndexAllowed(stroke.strokeIndex, hullFilter.allowedStrokeIndices)
    );
  }, [sessionQuery.data, hullFilter]);

  const deriveStrokeDurationMs = (strokes: StrokeSample[]): number => {
    const durations: number[] = [];
    for (const s of strokes) {
      if (s.driveTimeMs != null && s.recoveryTimeMs != null) {
        durations.push(s.driveTimeMs + s.recoveryTimeMs);
      }
    }
    if (durations.length > 0) {
      return durations.reduce((a, b) => a + b, 0) / durations.length;
    }
    return ARC_PHYSICS.STROKE_DURATION_MS;
  };

  if (!sessionId) {
    return (
      <ChartContainer title="Rowing Arc" subtitle={ARC_SUBTITLE} intrinsicHeight={flowLayout}>
        <p className="py-10 text-center text-sm text-rowing-gray-600">
          Select a session to view Rowing Arc.
        </p>
      </ChartContainer>
    );
  }

  if (sessionQuery.isLoading) {
    return (
      <ChartContainer
        title="Rowing Arc"
        subtitle={ARC_SUBTITLE}
        loading
        intrinsicHeight={flowLayout}
      >
        <div />
      </ChartContainer>
    );
  }

  if (sessionQuery.isError) {
    return (
      <ChartContainer
        title="Rowing Arc"
        subtitle={ARC_SUBTITLE}
        error="Could not load rowing arc data."
        intrinsicHeight={flowLayout}
      >
        <div />
      </ChartContainer>
    );
  }

  if (!filteredStrokes || filteredStrokes.length === 0) {
    return (
      <ChartContainer title="Rowing Arc" subtitle={ARC_SUBTITLE} intrinsicHeight={flowLayout}>
        <p className="py-10 text-center text-sm text-rowing-gray-600">
          {hullFilter?.isFilterActive
            ? 'No set strokes match the current hull-set filter for arc analysis.'
            : 'This session has no per-stroke arc samples yet.'}
        </p>
      </ChartContainer>
    );
  }

  const strokes = filteredStrokes;
  const seats = Array.from(
    new Set(strokes.map((stroke) => stroke.rowerPosition).filter((n) => Number.isFinite(n)))
  ).sort((a, b) => a - b);
  const initialSeat = rowerContext?.position ?? seats[0] ?? 1;
  const strokeDurationMs = deriveStrokeDurationMs(strokes);

  return (
    <ChartContainer
      title="Rowing Arc"
      subtitle={hullSetFilterSubtitle(ARC_SUBTITLE, hullFilter)}
      intrinsicHeight={flowLayout}
    >
      <ArcGraphCoachView
        sessionId={sessionId}
        strokes={strokes}
        availableSeats={seats.length > 0 ? seats : [1, 2, 3, 4, 5, 6, 7, 8]}
        initialSeat={initialSeat}
        strokeDurationMs={strokeDurationMs}
        embedded
      />
    </ChartContainer>
  );
};

export default RowingArcWrapper;
